import { supabase } from "@/integrations/supabase/client";
import { extractCityStateFromGeocodeResults } from "@/lib/locationDetection";
import { resolvePlaceName } from "@/lib/resolvePlaceName";

/**
 * Home area logic shared by every place a person sets their area: the
 * "Where are you based?" dialog, the onboarding location step, Profile's Home
 * Location setting (also inside the Add your area banner) and the Feed's
 * location prompt. Same region search, same current location detection, same
 * saved fields.
 */

/**
 * One region search result as google-places returns it today: place_id,
 * description ("Santa Monica, CA, USA") and types. The older
 * structured_formatting split is not sent any more, so it is optional here
 * and never relied on.
 */
export interface PlacePrediction {
  place_id: string;
  description: string;
  types?: string[];
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
}

export type HomeLocationSource = "manual" | "gps";

export interface HomeLocation {
  city: string;
  state: string | null;
  lat: number | null;
  lng: number | null;
  source: HomeLocationSource;
}

export async function searchRegions(query: string): Promise<PlacePrediction[]> {
  const { data, error } = await supabase.functions.invoke("google-places", {
    body: { action: "autocomplete", query, searchType: "regions" },
  });
  if (error) throw error;
  return data?.predictions || [];
}

function descriptionParts(prediction: PlacePrediction): string[] {
  return (prediction.description || "").split(",").map((part) => part.trim()).filter(Boolean);
}

/** The name the person picked: "Koreatown" from "Koreatown, Los Angeles, CA, USA". */
export function areaNameFromPrediction(prediction: PlacePrediction): string {
  return prediction.structured_formatting?.main_text?.trim() || descriptionParts(prediction)[0] || "";
}

/** The state code in a search result line such as "Los Angeles, CA, USA". */
function stateFromPrediction(prediction: PlacePrediction): string | null {
  return descriptionParts(prediction).slice(1).find((part) => /^[A-Z]{2}$/.test(part)) ?? null;
}

/**
 * Picked search result to a home location: the name from the result, the
 * point from the location only place details, and the state from the reverse
 * geocoder. Onboarding asks for the area before the account exists, so this
 * uses only the google-places lookups open to signed out people
 * (autocomplete, details with detailsMode "geometry", reverseGeocode). The
 * full details lookup is signed in only.
 */
export async function resolveRegionPrediction(prediction: PlacePrediction): Promise<HomeLocation> {
  const { data: details, error } = await supabase.functions.invoke("google-places", {
    body: { action: "details", detailsMode: "geometry", placeId: prediction.place_id },
  });
  if (error) throw error;
  const location = details?.result?.geometry?.location;
  const lat = typeof location?.lat === "number" ? location.lat : null;
  const lng = typeof location?.lng === "number" ? location.lng : null;
  const place = lat != null && lng != null ? await resolvePlaceName(lat, lng) : null;
  return {
    city: areaNameFromPrediction(prediction),
    state: place?.state || stateFromPrediction(prediction),
    lat,
    lng,
    source: "manual",
  };
}

/**
 * Current position to a home location. Throws the GeolocationPositionError
 * untouched so callers can tell a denied permission from other failures.
 */
export async function detectCurrentLocation(): Promise<HomeLocation> {
  if (!navigator.geolocation) throw new Error("Location is not available on this device");
  const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000,
    }),
  );
  const { latitude, longitude } = pos.coords;
  const { data, error } = await supabase.functions.invoke("google-places", {
    body: { action: "reverseGeocode", lat: latitude, lng: longitude },
  });
  if (error) throw error;
  const { city, state } = extractCityStateFromGeocodeResults(data?.results);
  if (!city) throw new Error("Could not determine city");
  return { city, state: state || null, lat: latitude, lng: longitude, source: "gps" };
}

export function isPermissionDenied(error: unknown): boolean {
  return (
    typeof GeolocationPositionError !== "undefined" &&
    error instanceof GeolocationPositionError &&
    error.code === error.PERMISSION_DENIED
  );
}

/** Removes the home area, including how it was set. */
export async function clearHomeLocation(userId: string): Promise<void> {
  const { error } = await supabase
    .from("user_preferences")
    .upsert(
      {
        user_id: userId,
        home_city: null,
        home_state: null,
        home_latitude: null,
        home_longitude: null,
        location_source: null,
        updated_at: new Date().toISOString(),
      } as never,
      { onConflict: "user_id" },
    );
  if (error) throw error;
}

/** The one write of the home area fields. */
export async function saveHomeLocation(userId: string, loc: HomeLocation): Promise<void> {
  const { error } = await supabase
    .from("user_preferences")
    .upsert(
      {
        user_id: userId,
        home_city: loc.city,
        home_state: loc.state,
        home_latitude: loc.lat,
        home_longitude: loc.lng,
        location_source: loc.source,
        updated_at: new Date().toISOString(),
      } as never,
      { onConflict: "user_id" },
    );
  if (error) throw error;
}
